import PriceDisplay from '@/components/PDP/PriceDisplay'
import { MAX_SIZE, RfqSchema } from '@/lib/schema/RfqFormSchema'
import axios from 'axios'
import clsx from 'clsx'
import { FormikProvider, useFormik } from 'formik'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React from 'react'
import { Customer } from 'shopify/api'
import { ProductWithCustomProps, RFQRequestColor } from 'types'
import { RFQ_COLOR } from 'types/constants'
import FixedBottomContainer from './FixedBottomContainer'
import { DateFormat, formatDate } from './utilities/DateUtils'
import WifCheckbox from './utilities/WifCheckbox'
import { WifInput } from './utilities/WifInput'
import WifButton from './WifButton'
import { notify } from './WifNotification'

interface RFQFormValues {
  fabricName: string
  fabricType: string
  orderQuantity: string
  requestColors: RFQRequestColor[]
  notes: string
  width: string
  format: string
  file: null
  isCustomOrder: boolean
}

const RFQModal: React.FC<{
  product?: ProductWithCustomProps
  imageSrc?: string
  perPrimaryUnitPrice?: number
  perPrimaryUnitComparisonPrice?: number
  customer: Customer
  onClose: () => void
  isCustomOrder: boolean
}> = ({
  imageSrc,
  perPrimaryUnitComparisonPrice,
  perPrimaryUnitPrice,
  product,
  customer,
  onClose,
  isCustomOrder,
}) => {
  const { t } = useTranslation()
  const [uploadedFileName, setUploadedFileName] = React.useState<string | null>('')
  const [uploadedFileSize, setUploadedFileSize] = React.useState<string | null>('')
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [tempFile, setTempFile] = React.useState<any>({})
  const router = useRouter()

  const sizeInKb = (size: number | null) => (size ? Math.floor(size / 1024) : 0)
  const convertToNumeralId = (shopifyId: string) => shopifyId.split('/')[4]
  const formik = useFormik<RFQFormValues>({
    initialValues: {
      fabricName: '',
      fabricType: '',
      orderQuantity: '',
      requestColors: [],
      notes: '',
      width: '',
      format: '',
      file: null,
      isCustomOrder: isCustomOrder,
    },
    validationSchema: RfqSchema,
    onSubmit: async () => {
      setIsLoading(true)
      const price = product?.priceRange.maxVariantPrice.amount
      const variantSku = product?.variants.nodes.find((variant) => variant.title === '1')?.sku
      try {
        const res = await axios.post('/api/quotation/submit-request', {
          itemName: isCustomOrder ? formik.values.fabricName : product?.title,
          quantity: Number(formik.values.orderQuantity),
          unit: 'kg',
          color: formik.values.requestColors,
          width: isCustomOrder ? '' : formik.values.width,
          format: isCustomOrder ? '' : formik.values.format,
          notes: formik.values.notes,
          customerName: customer.firstName,
          customerPhone: customer.phone,
          customerEmail: customer.email,
          customerCompany: customer.lastName,
          displayedPrice: isCustomOrder ? 0 : Number(price),
          compareAtPrice: isCustomOrder
            ? 0
            : Number(product?.compareAtPriceRange.maxVariantPrice.amount),
          priceUnit: isCustomOrder ? '' : '/Kg',
          customerShopifyId: convertToNumeralId(customer.id),
          sku: isCustomOrder ? '' : variantSku,
          category: isCustomOrder
            ? formik.values.fabricType.toLocaleLowerCase()
            : product?.productType.toLocaleLowerCase(),
          ...(tempFile.filename && {
            file: [
              {
                url: tempFile.url,
                filename: tempFile.filename,
              },
            ],
          }),
        })

        sendNotification(res)

        if (isCustomOrder) {
          notify({ type: 'info', text: t('rfq:Success_Request') })
          setTimeout(() => {
            router.push('/?successRfq=true')
          }, 3000)
        } else {
          notify({ type: 'info', text: t('rfq:Success_Request') })
          onClose()
        }
      } catch (err) {
        console.error(err)
        notify({
          type: 'info',
          text: t('rfq:Error.Processing_Request'),
        })
        setIsLoading(false)
      }
    },
  })

  const sendNotification = (res: any) => {
    const fields = res.data.record.fields
    const notification = {
      id: fields.ID,
      customer: {
        name: fields['Customer Name'],
        email: fields['Customer Email'],
        phone: fields['Customer Phone'],
      },
      product: {
        name: fields['Item Name'],
        quantity: fields.Quantity,
        unit: fields.Unit,
        count: 1,
        width: fields.Width,
        format: fields.Format,
        usage: fields.Notes,
        timeReceived: formatDate(new Date(fields['Time Received']), DateFormat.FULL_DAY_FORMAT),
      },
      isCustomOrder: isCustomOrder,
    }

    axios.post('/api/notification/admin/rfq-received', { notification })
    axios.post('/api/notification/buyer/rfq-received', { notification })
  }

  const submitFile = async (file: File) => {
    setIsLoading(true)
    const filename = encodeURIComponent(file.name)
    const res = await fetch(`/api/sign-file?file=${filename}?bucket=mto`)
    const { url, fields } = await res.json()
    const formData = new FormData()

    Object.entries({ ...fields, file }).forEach(([key, value]) => {
      formData.append(key, value as string)
    })

    try {
      const upload = await fetch(url, {
        method: 'POST',
        body: formData,
      })
      if (upload.ok) {
        notify({ type: 'info', text: t('rfq:Success_Upload_Image') })
      } else {
        notify({ type: 'info', text: t('rfq:Error.Fail_Upload_Image') })
      }
      setTempFile({
        filename,
        size: file.size,
        url: `${url}${filename}`,
        type: file.type,
      })

      setIsLoading(false)
    } catch (err) {
      setIsLoading(false)
    }
  }

  return (
    <FormikProvider value={formik}>
      <div className='py-[24px] h-[calc(100%-126px)] overflow-auto relative'>
        {imageSrc && (
          <div className='flex gap-[12px] mb-10 px-4' data-test='pdp-mto-rfq-form-product-summary'>
            <div className='w-[69px]'>
              <Image
                src={imageSrc}
                width={226}
                height={226}
                className='rounded-lg'
                data-test={`product-card-image-${product?.id}`}
                sizes='100vw'
                alt='Image'
              />
            </div>
            <div className='flex flex-col gap-[3px]'>
              <div className='text-sm font-normal'>{product?.title}</div>
              <div>
                <PriceDisplay
                  firstRowDisplayPrimaryPrice={perPrimaryUnitPrice}
                  firstRowDisplayPrimaryComparisonPrice={perPrimaryUnitComparisonPrice}
                  firstRowDisplayPrimaryUnit='Kg'
                />
              </div>
              <div className='text-xs font-light text-wif-neutral-80'>
                {t('pdp:mto.price_will_vary')}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={formik.handleSubmit} data-test='rfq-form'>
          {isCustomOrder && (
            <>
              <div className='px-4'>
                <WifInput
                  name='fabricName'
                  label={t('rfq:Field_Name.Fabric_Name')}
                  value={formik.values.fabricName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.fabricName}
                  errorMessage={t(formik.errors.fabricName!)}
                  customWrapperClass='flex-grow'
                  customInputClass='rounded-tr-none rounded-br-none'
                  disabled={isLoading}
                  placeholder={t('rfq:Placeholder_Fabric_Name')}
                />
              </div>
              <div className='px-4'>
                <WifInput
                  label={t('rfq:Field_Name.Fabric_Type')}
                  name='fabricType'
                  type='select'
                  value={formik.values.fabricType || ''}
                  selectPlaceholder={t('rfq:Placeholder_Fabric_Type')}
                  options={[
                    { name: t('rfq:Fabric_Type.Woven'), value: 'Woven' },
                    { name: t('rfq:Fabric_Type.Knit'), value: 'Knit' },
                    { name: t('rfq:Fabric_Type.Non-Woven'), value: 'Non Woven' },
                  ]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.fabricType}
                  errorMessage={t(formik.errors.fabricType!)}
                  disabled={isLoading}
                />
              </div>
            </>
          )}
          <div className='flex flex-row items-center px-4 '>
            <WifInput
              name='orderQuantity'
              label={t('rfq:Field_Name.Order_Quantity')}
              value={formik.values.orderQuantity}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={!!formik.errors.orderQuantity}
              errorMessage={t(formik.errors.orderQuantity!)}
              customWrapperClass='flex-grow'
              customInputClass='rounded-tr-none rounded-br-none'
              disabled={isLoading}
              inputUnit={'KG'}
            />
          </div>

          <div className='px-4 '>
            <span className='mb-[5px] inline-block text-sm'>{t('rfq:Field_Name.Color')}</span>

            <div
              role='group'
              aria-labelledby='checkbox-group'
              className='grid grid-cols-2 gap-4 mb-4'
            >
              <WifCheckbox
                id='color-Greige'
                name='requestColors'
                value={RFQ_COLOR.GREIGE}
                label={t('rfq:Word_Greige')}
                checked={
                  formik.values.requestColors.indexOf(RFQ_COLOR.GREIGE as RFQRequestColor) >= 0
                }
                disabled={isLoading}
              />
              <WifCheckbox
                id='color-White'
                name='requestColors'
                value={RFQ_COLOR.WHITE}
                label={t('rfq:Word_White')}
                checked={
                  formik.values.requestColors.indexOf(RFQ_COLOR.WHITE as RFQRequestColor) >= 0
                }
                disabled={isLoading}
              />
              <WifCheckbox
                id='color-Bright'
                name='requestColors'
                value={RFQ_COLOR.BRIGHT}
                label={t('rfq:Word_Bright')}
                checked={
                  formik.values.requestColors.indexOf(RFQ_COLOR.BRIGHT as RFQRequestColor) >= 0
                }
                disabled={isLoading}
              />
              <WifCheckbox
                id='color-Medium'
                name='requestColors'
                value={RFQ_COLOR.MEDIUM}
                label={t('rfq:Word_Medium')}
                checked={
                  formik.values.requestColors.indexOf(RFQ_COLOR.MEDIUM as RFQRequestColor) >= 0
                }
                disabled={isLoading}
              />
              <WifCheckbox
                id='color-Dark'
                name='requestColors'
                value={RFQ_COLOR.DARK}
                label={t('rfq:Word_Dark')}
                checked={
                  formik.values.requestColors.indexOf(RFQ_COLOR.DARK as RFQRequestColor) >= 0
                }
                disabled={isLoading}
              />
            </div>
          </div>

          {!isCustomOrder && (
            <>
              <div className='px-4'>
                <WifInput
                  label={t('rfq:Field_Name.Width')}
                  name='width'
                  value={formik.values.width}
                  placeholder={t('rfq:Placeholder_Width')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={false}
                  errorMessage=''
                  disabled={isLoading}
                />
              </div>

              <div className='px-4'>
                <WifInput
                  label={t('rfq:Field_Name:Format')}
                  name='format'
                  type='select'
                  value={formik.values.format || ''}
                  selectPlaceholder={t('rfq:Placeholder_Format')}
                  options={[
                    { name: t('rfq:Word_Cut'), value: 'Cut' },
                    { name: t('rfq:Word_Tubular'), value: 'Tubular' },
                  ]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={false}
                  errorMessage=''
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          <div className='px-4'>
            <WifInput
              label={t('rfq:Field_Name.Usability')}
              name='notes'
              value={formik.values.notes}
              placeholder={t('rfq:Placeholder_Usability')}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={!!formik.errors.notes}
              errorMessage={t(formik.errors.notes!)}
              customWrapperClass='flex-grow'
              customInputClass='rounded-tr-none rounded-br-none'
              disabled={isLoading}
            />
          </div>

          <div className='px-4 '>
            <span className='mb-[5px] inline-block text-sm'>{t('rfq:Field_Name.Upload')}</span>
            <input
              accept='image/png, image/jpg, image/jpeg'
              type='file'
              name='file'
              id='file'
              data-test='rfq-file-upload-field'
              className='opacity-0 overflow-hidden -z-10 absolute  w-[0.01px] h-[0.01px]'
              onChange={async (e) => {
                if (e.currentTarget) {
                  formik.setFieldValue('file', e.currentTarget.files![0])
                  setUploadedFileName(e.currentTarget.files && e.currentTarget.files[0]?.name)
                  setUploadedFileSize(
                    `${sizeInKb(e.currentTarget.files && e.currentTarget.files[0]?.size)}KB`
                  )
                  if (!(e.currentTarget.files && e.currentTarget.files[0]?.size > MAX_SIZE))
                    await submitFile(e.currentTarget.files![0])
                }
              }}
            />
            <label
              htmlFor='file'
              data-test='rfq-file-upload-label'
              className={clsx(
                'flex items-center justify-center w-full border p-4 rounded-lg cursor-pointer',
                formik.errors.file && 'border-error-red'
              )}
            >
              <Image src='/icons/ic-upload.svg' width={24} height={24} alt='Upload Icon' />
              <span className='inline-block max-w-1/3 overflow-ellipsis overflow-hidden whitespace-nowrap mr-2 ml-3'>
                {uploadedFileName || t('rfq:Field_Name.Upload')}
              </span>
              {uploadedFileSize && `(${uploadedFileSize})`}
            </label>
            <span className='text-error-red text-xs'>
              {t(formik.errors.file as string, {
                maxSize: MAX_SIZE / 1024,
              })}
            </span>
          </div>

          <FixedBottomContainer>
            <WifButton
              id='rfq-submit'
              text={t('rfq:Word_Request')}
              type='primary'
              handleClick={async () => {}}
              iconSrc='/icons/ic-bag-white_32x32.svg'
              disabled={isLoading}
            />
          </FixedBottomContainer>
        </form>
      </div>
    </FormikProvider>
  )
}

export default RFQModal
